import { Box } from "@mui/material";
import { DragIndicator } from "@mui/icons-material";
import { useSortable, defaultAnimateLayoutChanges } from "@dnd-kit/sortable";
import type { AnimateLayoutChanges } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import React from "react";
import MortgageSimulator from "../simulator";
import SimulationCard from "./SimulationCard";

// Disable animations when items are reordered to prevent double-shuffle effect
const animateLayoutChanges: AnimateLayoutChanges = (args) => {
  const { isSorting, wasDragging } = args;
  if (isSorting || wasDragging) {
    return false;
  }
  return defaultAnimateLayoutChanges(args);
};

interface SortableSimulationCardProps {
  id: string;
  sim: MortgageSimulator;
  moreExpensive: number | null;
  idx: number;
  handleEdit?: (idx: number) => void;
  handleDuplicate?: (idx: number) => void;
  handleDelete?: (idx: number) => void;
  toggleExpanded: () => void;
  deleteDisabled?: boolean;
}

export default function SortableSimulationCard({
  id,
  sim,
  moreExpensive,
  idx,
  handleEdit,
  handleDuplicate,
  handleDelete,
  toggleExpanded,
  deleteDisabled = false,
}: SortableSimulationCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, animateLayoutChanges });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        "&:hover .drag-handle": {
          opacity: 1,
        },
      }}
    >
      <Box
        className="drag-handle"
        sx={{
          position: "absolute",
          top: 4,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "grab",
          touchAction: "none",
          px: 2,
          py: 1,
          opacity: 0,
          transition: "opacity 0.2s ease-in-out",
        }}
        {...attributes}
        {...listeners}
      >
        <DragIndicator fontSize="small" sx={{ color: "text.secondary" }} />
      </Box>
      <SimulationCard
        sim={sim}
        moreExpensive={moreExpensive}
        idx={idx}
        handleEdit={handleEdit}
        handleDuplicate={handleDuplicate}
        handleDelete={handleDelete}
        toggleExpanded={toggleExpanded}
        deleteDisabled={deleteDisabled}
      />
    </Box>
  );
}
